import React, { useState } from "react";

const isProbablyExternalDomain = (value) => {
  const v = String(value || "").trim();
  if (!v) return false;
  if (/^https?:\/\//i.test(v)) return true;
  if (/^www\./i.test(v)) return true;
  if (/^[^\s/]+\.[^\s/]{2,}([/?#].*)?$/i.test(v)) return true;
  return false;
};

const normalizeHref = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return "";

  const lower = v.toLowerCase();

  // keep mail / tel
  if (lower.startsWith("mailto:")) return v;
  if (lower.startsWith("tel:")) return v;

  // phone number -> WhatsApp
  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  // wa:972... or whatsapp:972...
  if (lower.startsWith("wa:") || lower.startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  // malformed protocol fixes
  if (/^https\/\/:/i.test(v)) {
    return v.replace(/^https\/\/:/i, "https://");
  }

  if (/^http\/\/:/i.test(v)) {
    return v.replace(/^http\/\/:/i, "http://");
  }

  if (/^https:\/(?!\/)/i.test(v)) {
    return v.replace(/^https:\/(?!\/)/i, "https://");
  }

  if (/^http:\/(?!\/)/i.test(v)) {
    return v.replace(/^http:\/(?!\/)/i, "http://");
  }

  // already valid absolute URL
  if (/^https?:\/\//i.test(v)) return v;

  // wa.me direct domain
  if (/^wa\.me\//i.test(v)) return `https://${v}`;

  // www.domain.com
  if (/^www\./i.test(v)) return `https://${v}`;

  // plain domain like example.com or sub.example.com/path
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#].*)?$/i.test(v)) {
    return `https://${v}`;
  }

  // internal path
  if (v.startsWith("/")) return v;

  // fallback internal slug/path
  return `/${v}`;
};

const goToLink = (raw) => {
  const href = normalizeHref(raw);
  if (!href) return;

  const isExternal =
    /^https?:\/\//i.test(href) ||
    /^mailto:/i.test(href) ||
    /^tel:/i.test(href);

  if (isExternal) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const pickAlign = (style, config, fallback = "center") => {
  const v = String(style?.textAlign || style?.alignment || config?.alignment || "")
    .toLowerCase()
    .trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return fallback;
};

const renderActionIcon = (service) => {
  if (service?.buttonIcon) {
    return <img src={service.buttonIcon} alt="" className="w-5 h-5 object-contain" />;
  }

  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7v7m0-7L10 14m-4 0H3v7h7" />
    </svg>
  );
};

const ServiceSection2 = ({ config, style }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const isRTL = (config?.direction || "ltr") === "rtl";
  const align = pickAlign(style, config, "center");
  const alignMap = { left: "text-left", center: "text-center", right: "text-right" };
  const alignClass = alignMap[align] || "text-center";

  const services = Array.isArray(config.services)
    ? config.services
    : [
        {
          id: 1,
          title: "Business Consulting",
          fullDescription:
            "Our experienced consultants work closely with your team to identify opportunities, streamline processes, and implement solutions that deliver measurable results.",
          image:
            "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1350&q=80",
          price: "From $2,500",
          duration: "2-4 weeks",
          link: "example.com",
          buttonLink: "example.com",
          buttonIcon: "",
          features: ["Market Analysis", "Process Optimization", "Growth Strategy"],
        },
        {
          id: 2,
          title: "Digital Transformation",
          fullDescription:
            "Embrace the digital future with comprehensive transformation services, from legacy system migration to implementing new workflows.",
          image:
            "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1350&q=80",
          price: "Custom pricing",
          duration: "3-6 months",
          link: "wa:972501234567",
          buttonLink: "wa:972501234567",
          buttonIcon: "",
          features: ["Technology Audit", "System Integration", "Digital Strategy"],
        },
      ];

  const safeActiveIndex =
    services.length > 0 ? Math.min(activeIndex, services.length - 1) : 0;

  const activeService = services[safeActiveIndex];
  const bg = buildBg(style, "bg-gradient-to-br from-gray-50 to-white");

  const nextSlide = () => setActiveIndex((p) => (p + 1) % services.length);
  const prevSlide = () => setActiveIndex((p) => (p - 1 + services.length) % services.length);

  if (!services.length) return null;

  const actionLink = activeService?.buttonLink || activeService?.link || "";

  return (
    <section dir={config?.direction || "ltr"} style={bg.style} className={`py-20 px-4 ${bg.className}`}>
      <div className="container mx-auto max-w-7xl">
        <div className={`${alignClass} mb-16`}>
          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            {config.title || "How We Help Your Business"}
          </h2>
          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {config.subtitle || "Explore our comprehensive range of business solutions"}
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <div
            style={{
              backgroundColor: style?.servicePanelBg || undefined,
              borderColor: style?.servicePanelBorder || undefined,
            }}
            className={`bg-white rounded-3xl shadow-2xl overflow-hidden ${
              style?.servicePanelBorder ? "border" : ""
            }`}
          >
            <div className="grid md:grid-cols-2">
              <div className="relative h-64 md:h-auto">
                <img
                  src={activeService.image}
                  alt={activeService.title}
                  className="w-full h-full object-cover"
                />
                <div
                  style={{ backgroundColor: style?.serviceImageOverlayColor || undefined }}
                  className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent md:hidden"
                />
              </div>

              <div className={`p-8 md:p-10 ${alignClass}`}>
                <div
                  style={{ color: style?.serviceMetaTextColor || undefined }}
                  className="flex items-center gap-2 text-sm text-blue-600 mb-2 justify-start"
                >
                  <span className="font-semibold">Featured Service</span>
                  <span
                    style={{ backgroundColor: style?.serviceMetaTextColor || undefined }}
                    className="w-1 h-1 rounded-full"
                  />
                  <span>{activeService.duration}</span>
                </div>

                <h3
                  style={{ color: style?.serviceCardTitleColor || undefined }}
                  className="text-3xl font-bold text-gray-900 mb-4"
                >
                  {activeService.title}
                </h3>

                <p
                  style={{ color: style?.serviceCardDescriptionColor || undefined }}
                  className="text-gray-600 mb-4 leading-relaxed"
                >
                  {activeService.fullDescription}
                </p>

                <div className="mb-6">
                  <h4
                    style={{ color: style?.serviceCardTitleColor || undefined }}
                    className="font-semibold text-gray-900 mb-3"
                  >
                    Key Benefits:
                  </h4>
                  <ul className="space-y-2">
                    {activeService.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className={`flex items-center gap-2 ${
                          isRTL ? "flex-row-reverse justify-end" : ""
                        }`}
                        style={{ color: style?.serviceFeatureTextColor || undefined }}
                      >
                        <svg
                          style={{ color: style?.serviceFeatureIconColor || undefined }}
                          className="w-4 h-4 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between gap-6">
                  <div>
                    <p
                      style={{ color: style?.serviceMutedTextColor || undefined }}
                      className="text-sm text-gray-500"
                    >
                      Starting at
                    </p>
                    <p
                      style={{ color: style?.servicePriceColor || undefined }}
                      className="text-2xl font-bold text-gray-900"
                    >
                      {activeService.price}
                    </p>
                  </div>

                  {actionLink ? (
                    <button
                      type="button"
                      onClick={() => goToLink(actionLink)}
                      style={{
                        backgroundColor: style?.primaryBtnBg || undefined,
                        color: style?.primaryBtnText || undefined,
                      }}
                      className={`inline-flex items-center justify-center rounded-full w-12 h-12 transition duration-300 ${
                        style?.primaryBtnBg ? "hover:opacity-90" : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                      aria-label={activeService?.title || "Open link"}
                      title={activeService?.title || "Open link"}
                    >
                      {renderActionIcon(activeService)}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={isRTL ? nextSlide : prevSlide}
            style={{
              backgroundColor: style?.serviceNavBtnBg || undefined,
              borderColor: style?.serviceNavBtnBorder || undefined,
              color: style?.serviceNavBtnIcon || undefined,
            }}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center transition-colors ${
              style?.serviceNavBtnBorder ? "border" : ""
            }`}
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            type="button"
            onClick={isRTL ? prevSlide : nextSlide}
            style={{
              backgroundColor: style?.serviceNavBtnBg || undefined,
              borderColor: style?.serviceNavBtnBorder || undefined,
              color: style?.serviceNavBtnIcon || undefined,
            }}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center transition-colors ${
              style?.serviceNavBtnBorder ? "border" : ""
            }`}
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {services.map((_, index) => {
            const isActive = index === safeActiveIndex;
            return (
              <button
                key={index}
                type="button"
                onClick={() => setActiveIndex(index)}
                style={{
                  backgroundColor: isActive
                    ? style?.serviceDotActive || undefined
                    : style?.serviceDotInactive || undefined,
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  isActive ? "w-8 bg-blue-600" : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServiceSection2;